import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { baseUrl } from "../../../utils/apis";
import { returnErrors } from "../../../store/reducers/error";
import { deAuthenticate } from "../../../store/reducers/authSlice";
import { PURGE_Brand } from "../../../store/reducers/brandSlice";
import { clearPersistedData } from "../../../store/configureStore";
import Spinner from "../../../reusableComponents/Spinner";
import PasswordRequirements from "../../../reusableComponents/PasswordRequirements";
import { PASSWORD_RULES_MESSAGE, validatePassword } from "../../../utils/helpers/string";
import "../../../components/Auth/ForcePasswordChange/style.scss";

const TOKEN_KEY = "ee-pw-reset-token";

// Read once. public/index.html moves the token out of the URL fragment into
// window.__pwResetToken before any tracking script runs; the fragment is the
// fallback. It's also kept in this tab's sessionStorage so a reload (or a phone
// browser restoring the tab after an app switch) doesn't lose it, and removed
// as soon as the link is used or turns out to be invalid.
const readToken = () => {
  const fresh = window.__pwResetToken || new URLSearchParams(window.location.hash.slice(1)).get("token");
  try {
    if (fresh) sessionStorage.setItem(TOKEN_KEY, fresh);
    return fresh || sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    return fresh || "";
  }
};

const forgetToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // storage unavailable: nothing was saved
  }
};

// Opened from the reset email. Not behind login: the link itself proves the user owns the inbox.
function SetNewPassword() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [token] = useState(readToken);
  const [stage, setStage] = useState("checking"); // checking | form | invalid | error | done
  const [checkError, setCheckError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);

  const checkLink = useCallback(() => {
    if (!token) {
      setStage("invalid");
      return;
    }
    setStage("checking");
    axios
      .post(`${baseUrl}/auth/password-reset/verify/`, { token })
      .then((res) => {
        setMaskedEmail(res.data?.email || "");
        setStage("form");
      })
      .catch((error) => {
        if (error.response?.data?.detail === "link-invalid-or-expired") {
          forgetToken();
          setStage("invalid");
          return;
        }
        // Offline, server error or rate limit: the link may be fine, so don't call it expired
        setCheckError(
          error.response?.status === 429
            ? "Too many attempts. Please wait a few minutes and try again."
            : "We couldn't check your link. Please check your connection and try again.",
        );
        setStage("error");
      });
  }, [token]);

  useEffect(() => {
    delete window.__pwResetToken;
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => {
    checkLink();
  }, [checkLink]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (!validatePassword(form.new_password)) {
      setErrors([PASSWORD_RULES_MESSAGE]);
      return;
    }
    if (form.new_password !== form.confirm) {
      setErrors(["Passwords do not match."]);
      return;
    }

    setIsSpinner(true);
    try {
      await axios.post(`${baseUrl}/auth/password-reset/confirm/`, { token, new_password: form.new_password });
      forgetToken();
      // Every session was ended on the server; drop this browser's too
      if (isAuthenticated) {
        dispatch(deAuthenticate());
        dispatch(PURGE_Brand());
        await clearPersistedData();
      }
      setStage("done");
    } catch (error) {
      const data = error.response?.data || {};
      if (data.detail === "invalid-password" && Array.isArray(data.errors)) {
        setErrors(data.errors);
      } else if (data.detail === "link-invalid-or-expired") {
        forgetToken();
        setStage("invalid");
      } else {
        dispatch(returnErrors(typeof data.detail === "string" && error.response?.status === 429 ? data.detail : "Something went wrong. Please try again.", 400));
      }
    } finally {
      setIsSpinner(false);
    }
  };

  return (
    <div className="force_password_change">
      {stage === "checking" && (
        <div className="force_password_change__card">
          <p>Checking your link…</p>
        </div>
      )}

      {stage === "invalid" && (
        <div className="force_password_change__card">
          <h1>This link has expired</h1>
          <p>Reset links expire after 30 minutes and stop working once your password has been reset. Please request a new one.</p>
          <Link to="/forgot-password">
            <button type="button">Request a new link</button>
          </Link>
        </div>
      )}

      {stage === "error" && (
        <div className="force_password_change__card">
          <h1>We couldn't check your link</h1>
          <p>{checkError}</p>
          <button type="button" onClick={checkLink}>
            Try again
          </button>
        </div>
      )}

      {stage === "done" && (
        <div className="force_password_change__card">
          <h1>Password updated</h1>
          <p>Your password has been changed and you've been signed out of all devices. You can now log in with your new password.</p>
          <Link to="/signin">
            <button type="button">Log in</button>
          </Link>
        </div>
      )}

      {stage === "form" && (
        <form className="force_password_change__card" onSubmit={handleSubmit}>
          <h1>Set a new password</h1>
          <p>
            Choose a new password{maskedEmail ? ` for ${maskedEmail}` : ""}. Use one you don't use on any other website.
          </p>

          <label htmlFor="new_password">New password</label>
          <input id="new_password" name="new_password" type={show ? "text" : "password"} autoComplete="new-password" value={form.new_password} onChange={handleChange} />
          <PasswordRequirements password={form.new_password} />

          <label htmlFor="confirm">Confirm new password</label>
          <input id="confirm" name="confirm" type={show ? "text" : "password"} autoComplete="new-password" value={form.confirm} onChange={handleChange} />

          <label className="force_password_change__show">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show passwords
          </label>

          {errors.length > 0 && (
            <ul className="force_password_change__errors">
              {errors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}

          <button type="submit" disabled={isSpinner}>
            {isSpinner ? <Spinner /> : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}

export default SetNewPassword;
